import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MoreVertical, History, Plus } from 'lucide-react';
import './ComponentActionsDropdown.css';

export type EntityType = 'level' | 'room';

interface EntityActionsDropdownProps {
    buildingKey: string;
    entityKey: string;
    entityType: EntityType;
    /** Button size variant (default: 'small') */
    size?: 'small' | 'large';
    /** Shown as "Ny våning" when given (entityType 'level', admin only). */
    onCreateLevel?: () => void;
}

/**
 * Actions dropdown for levels and rooms.
 * Shows History action (can be extended with more actions).
 */
const EntityActionsDropdown: React.FC<EntityActionsDropdownProps> = ({
    buildingKey,
    entityKey,
    entityType,
    size = 'small',
    onCreateLevel,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleCreateLevel = () => {
        onCreateLevel?.();
        setIsOpen(false);
    };

    const buttonClass = size === 'large'
        ? 'actions-dropdown-button actions-dropdown-button--large'
        : 'actions-dropdown-button';

    const iconSize = size === 'large' ? 20 : 18;

    return (
        <div ref={dropdownRef} className="actions-dropdown">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`${buttonClass} ${isOpen ? 'actions-dropdown-button--active' : ''}`}
                title="Åtgärder"
            >
                <MoreVertical size={iconSize} color="#666" />
            </button>

            {isOpen && (
                <div className="actions-dropdown-menu">
                    <Link
                        to={`/building/${buildingKey}/${entityType}/${entityKey}/history`}
                        onClick={() => setIsOpen(false)}
                        className="actions-dropdown-item"
                    >
                        <History size={18} color="#6c757d" />
                        <span>Historik</span>
                    </Link>
                    {entityType === 'level' && onCreateLevel && (
                        <button
                            type="button"
                            onClick={handleCreateLevel}
                            className="actions-dropdown-item"
                        >
                            <Plus size={18} color="#6c757d" />
                            <span>Ny våning</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default EntityActionsDropdown;
